import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for boolean validation
 */
export interface BooleanValidatorOptions extends BaseValidatorOptions {}

/**
 * Validate a boolean value
 * Accepts: true/false, yes/no, 1/0, on/off (case-insensitive)
 * @param value - The string representation of a boolean
 * @param options - Validation options
 * @returns Validation result with the boolean value and optional error (never null)
 *
 * @example
 * ```typescript
 * validateBoolean("true");  // { value: true }
 * validateBoolean("yes");   // { value: true }
 * validateBoolean("1");     // { value: true }
 * validateBoolean("on");    // { value: true }
 * validateBoolean("false"); // { value: false }
 * validateBoolean("no");    // { value: false }
 * validateBoolean("0");     // { value: false }
 * validateBoolean("off");   // { value: false }
 * validateBoolean("maybe"); // { value: false, error: { field: "value", message: "...", severity: "warning" } }
 * ```
 */
export function validateBoolean(value: string, options: BooleanValidatorOptions = {}): ValidationResult<boolean> {
  const { field = "value" } = options;
  const trimmedValue = value.trim();
  const lowerValue = trimmedValue.toLowerCase();

  if (["true", "yes", "1", "on"].includes(lowerValue)) {
    return { value: true };
  }

  if (["false", "no", "0", "off"].includes(lowerValue)) {
    return { value: false };
  }

  return {
    value: false,
    error: {
      field,
      message: `"${field}" does not match required boolean pattern: ${value}`,
      severity: "warning",
    },
  };
}
